import {
  saveViewPreference,
  type ViewPreferencesMap,
} from "@/features/settings/api/viewPreferences.api";

/**
 * "Lead document or deal document?" — the answer this user wants remembered.
 *
 *   ask  — prompt on every upload (the default, and what everyone starts on)
 *   lead — always file uploads on the LEAD (the person's own paperwork)
 *   deal — always file uploads on the DEAL that is open at the time
 *
 * The prompt only ever appears when the lead HAS deals; a lead with none has
 * nothing to choose between, so it uploads straight to the lead as it always
 * has.
 *
 * PER USER, not per workspace: two counsellors working the same student file
 * differently is normal, and the server refuses to mirror this key into the
 * tenant defaults even when an admin saves it (PERSONAL_ONLY_VIEW_KEYS in
 * `view-preference.service.ts`).
 */
export type DocumentScopeMode = "ask" | "lead" | "deal";

/** Server-side viewKey. Must match the server's ALLOWED_VIEW_KEYS entry. */
export const DOCUMENT_SCOPE_VIEW_KEY = "documentScopePrompt";

/**
 * localStorage mirror. The DB row is the source of truth, but it only lands
 * after the boot `/core/view-preferences` fetch — the mirror is what makes
 * the choice stick on a hard refresh straight into a lead drawer.
 */
const LS_KEY = "xale_document_scope_mode";

function isMode(v: unknown): v is DocumentScopeMode {
  return v === "ask" || v === "lead" || v === "deal";
}

function readMirror(): DocumentScopeMode {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return isMode(raw) ? raw : "ask";
  } catch {
    return "ask";
  }
}

function writeMirror(mode: DocumentScopeMode) {
  try {
    localStorage.setItem(LS_KEY, mode);
  } catch {
    // Private window / storage disabled — the DB row still carries it.
  }
}

let currentMode: DocumentScopeMode = readMirror();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

export function getDocumentScopeMode(): DocumentScopeMode {
  return currentMode;
}

export function subscribeDocumentScopeMode(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Apply the DB row at boot. Called from the verify hydrate alongside the
 * other per-user preferences. Never writes back — that would echo a save.
 */
export function hydrateDocumentScopeMode(preferences: ViewPreferencesMap) {
  const raw = (preferences?.[DOCUMENT_SCOPE_VIEW_KEY] as { mode?: unknown })
    ?.mode;
  if (!isMode(raw) || raw === currentMode) return;
  currentMode = raw;
  writeMirror(raw);
  emit();
}

/**
 * Change the remembered answer. Applies locally first so the next upload
 * already obeys it, then persists; a failed save leaves the session correct
 * and the mirror carries it to the next one.
 */
export async function setDocumentScopeMode(
  mode: DocumentScopeMode
): Promise<void> {
  if (mode !== currentMode) {
    currentMode = mode;
    writeMirror(mode);
    emit();
  }
  try {
    await saveViewPreference(DOCUMENT_SCOPE_VIEW_KEY, { mode });
  } catch {
    // Best-effort: the mirror already holds it for this browser.
  }
}

/** Clear on sign-out so the next person on this machine starts on "ask". */
export function resetDocumentScopeMode() {
  currentMode = "ask";
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    // ignore
  }
  emit();
}
